/**
 * Virtual keys — queries + mutations.
 *
 * Tenant-scoped. Create and revoke are step-up gated server-side (agents.manage), so both go
 * through withStepUp: issuing a bearer credential that acts as a named principal is exactly the
 * class of act that should cost a fresh passkey touch.
 *
 * THE SECRETS RULE, and it is the reason this file reads the way it does: the create mutation
 * resolves with the plaintext key, and that value is NEVER written to the cache. onSuccess
 * invalidates the roster so the new row appears; it does not setQueryData with the response,
 * because that response carries a secret and a cache is a place secrets outlive the moment they
 * were needed. The caller holds the plaintext in component state for one render and drops it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import {
  createVirtualKeyResponseSchema,
  virtualKeysListResponseSchema,
  type CreateVirtualKeyInput,
  type CreateVirtualKeyResponse,
  type VirtualKeysListResponse,
} from "./schemas";

function tenantBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents`;
}

const rosterKey = (tenantID: string, agentID?: string) =>
  ["agents", "virtual-keys", tenantID, agentID ?? "all"] as const;

export async function fetchVirtualKeys(
  tenantID: string,
  agentID?: string,
): Promise<VirtualKeysListResponse> {
  const path = agentID
    ? `${tenantBase(tenantID)}/${encodeURIComponent(agentID)}/virtual-keys`
    : `${tenantBase(tenantID)}/virtual-keys`;
  const raw = await apiFetch<unknown>(path);
  return virtualKeysListResponseSchema.parse(raw);
}

/**
 * The roster. Includes revoked and expired rows on purpose: an operator auditing what a workload
 * could reach needs the dead ones as much as the live ones, and "when did this stop working" is
 * a question only the dead rows answer.
 *
 * No polling. These records change when a human acts on them, not on their own — the one field
 * that moves by itself, last_used_at, is observability and is never worth a request per minute.
 */
export function useVirtualKeysQuery(tenantID: string, agentID?: string) {
  return useQuery<VirtualKeysListResponse, ApiError>({
    queryKey: rosterKey(tenantID, agentID),
    queryFn: () => fetchVirtualKeys(tenantID, agentID),
    enabled: tenantID.length > 0,
    staleTime: 30_000,
  });
}

function toWireBody(input: CreateVirtualKeyInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    standing_delegation_id: input.standing_delegation_id,
    allowed_destinations: input.allowed_destinations,
  };
  if (input.label) body.label = input.label;
  if (input.expires_at) body.expires_at = input.expires_at;
  return body;
}

/**
 * Create. Resolves with the plaintext key exactly once.
 *
 * The response is deliberately NOT written into any cache — see the file header. The roster is
 * invalidated so the new row appears, and the secret lives only in the promise the caller awaits.
 */
export function useCreateVirtualKeyMutation(tenantID: string, agentID: string) {
  const qc = useQueryClient();
  return useMutation<CreateVirtualKeyResponse, ApiError, CreateVirtualKeyInput>({
    mutationFn: (input) =>
      withStepUp(async () => {
        const raw = await apiFetch<unknown>(
          `${tenantBase(tenantID)}/${encodeURIComponent(agentID)}/virtual-keys`,
          // Not pre-stringified: apiFetch serializes `body` itself.
          { method: "POST", body: toWireBody(input) },
        );
        return createVirtualKeyResponseSchema.parse(raw);
      }),
    onSuccess: () => {
      // Invalidate only. NEVER setQueryData here: the resolved value carries the plaintext.
      void qc.invalidateQueries({ queryKey: ["agents", "virtual-keys", tenantID] });
    },
  });
}

/**
 * Revoke. Immediate and total — the next call presenting the key fails to resolve, with no TTL
 * to wait out. That is the one way this credential is stronger than the SaaS key it replaced.
 */
export function useRevokeVirtualKeyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { keyId: string; reason?: string }>({
    mutationFn: ({ keyId, reason }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${tenantBase(tenantID)}/virtual-keys/${encodeURIComponent(keyId)}/revoke`,
          // Not pre-stringified: apiFetch serializes `body` itself.
          { method: "POST", body: reason ? { reason } : {} },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agents", "virtual-keys", tenantID] });
    },
  });
}
